// Звуки требуют пользовательского жеста: браузер может блокировать автовоспроизведение.
import { getSoundChoice, type SoundChoice } from './lib/soundChoice';
let ctx: AudioContext | null = null;

function getCtx(): AudioContext {
  if (!ctx) ctx = new AudioContext();
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

/** Нота с быстрой атакой и плавным затуханием (щипковая/маримба-огибающая) — в отличие от
 *  плоского gain.value константы, resource звучит с характерным деревянным "щипком" вместо
 *  резкого прямоугольного вкл/выкл синуса. attackMs/releaseMs — доли envelope внутри durationMs
 *  (release отсчитывается от конца ноты, как и в offline-генерации, которой выбирали вариант). */
function pluck(freq: number, durationMs: number, delayMs = 0, gain = 0.28, attackMs = 5, releaseMs = durationMs * 0.6) {
  try {
    const ac = getCtx();
    const osc = ac.createOscillator();
    const g = ac.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    osc.connect(g).connect(ac.destination);
    const t0 = ac.currentTime + delayMs / 1000;
    const dur = durationMs / 1000;
    const holdUntil = t0 + Math.max(0, (durationMs - releaseMs) / 1000);
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(gain, t0 + attackMs / 1000);
    g.gain.setValueAtTime(gain, holdUntil);
    g.gain.linearRampToValueAtTime(0, t0 + dur);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
    return () => {
      try { osc.stop(); } catch { /* уже остановлен */ }
      osc.disconnect();
      g.disconnect();
    };
  } catch {
    /* AudioContext недоступен — тихо игнорируем */
  }
}

/** Зацикленный сигнал входящего звонка — маримба-арпеджио C5→E5→G5→C6 (выбран пользователем
 *  среди вариантов на прослушивание, см. docs/… ringtone artifact). Вызвать возвращённую
 *  функцию, чтобы остановить. */
export function startRingtone(choice: SoundChoice = getSoundChoice('calls')): () => void {
  if (choice === 'silent') return () => {};
  let notes: (() => void)[] = [];
  let stopped = false;
  const CYCLE_MS = 1720; // 180×3 + 280 (ноты) + 900 (тишина) — тот же таймлайн, что и в превью
  const ring = () => {
    if (stopped) return;
    notes.forEach((stop) => stop());
    notes = [
      pluck(523.25, 180, 0, 0.28),
      pluck(659.25, 180, 180, 0.28),
      pluck(783.99, 180, 360, 0.28),
      pluck(1046.5, 280, 540, 0.28, 5, 200),
    ].filter((stop): stop is () => void => stop !== undefined);
  };
  ring();
  const id = window.setInterval(ring, CYCLE_MS);
  return () => {
    if (stopped) return;
    stopped = true;
    clearInterval(id);
    notes.forEach((stop) => stop());
    notes = [];
  };
}

/** Гудок исходящего вызова: 425 Гц, секунда звука и четыре секунды паузы.
 * Остановка отменяет и таймер, и уже запланированный звук, в том числе при suspended context. */
export function startRingback(): () => void {
  let stopped = false;
  let oscillator: OscillatorNode | null = null;
  let gain: GainNode | null = null;
  const stopTone = () => {
    try { oscillator?.stop(); } catch { /* уже остановлен */ }
    oscillator?.disconnect();
    gain?.disconnect();
    oscillator = null;
    gain = null;
  };
  const ring = () => {
    if (stopped) return;
    stopTone();
    try {
      const ac = getCtx();
      oscillator = ac.createOscillator();
      gain = ac.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = 425;
      oscillator.connect(gain).connect(ac.destination);
      const now = ac.currentTime;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.15, now + 0.01);
      gain.gain.setValueAtTime(0.15, now + 0.99);
      gain.gain.linearRampToValueAtTime(0, now + 1);
      oscillator.start(now);
      oscillator.stop(now + 1.02);
    } catch {
      // Отсутствие Web Audio не должно мешать звонку.
      stopTone();
    }
  };
  ring();
  const timer = window.setInterval(ring, 5000);
  return () => {
    if (stopped) return;
    stopped = true;
    clearInterval(timer);
    stopTone();
  };
}

let messageAudio: HTMLAudioElement | null = null;

/** Один короткий сигнал; новый сигнал заменяет предыдущий, чтобы не накладываться. */
export function playMessageSound(choice: SoundChoice = getSoundChoice('messages')): () => void {
  if (choice === 'silent') return () => {};
  try {
    messageAudio?.pause();
    const audio = new Audio('/sounds/notification.ogg');
    messageAudio = audio;
    void audio.play().catch(() => {});
    return () => {
      audio.pause();
      audio.currentTime = 0;
      if (messageAudio === audio) messageAudio = null;
    };
  } catch {
    // Недоступное аудио не мешает получению сообщений.
    return () => {};
  }
}
